import {
	ChangeDetectionStrategy,
	Component,
	Input,
	ElementRef,
	AfterViewInit,
	OnChanges,
	SimpleChanges
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { BaseMarker, Marker, TagCategoryName } from '@go-modules/interfaces/markers/marker';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';

export const AI_MARKER_CATEGORY_ORDER = [
	TagCategoryName.PRAISE,
	TagCategoryName.IMPROVE,
	TagCategoryName.GENERAL
];

type MarkerDisplayItem = BaseMarker & Partial<Marker>;

@Component({
	selector: 'marker-display',
	templateUrl: './marker-display.component.html',
	styleUrls: ['./marker-display.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class MarkerDisplayComponent implements AfterViewInit, OnChanges {
	@Input() public markers: MarkerDisplayItem[] = [];
	@Input() public withCount: boolean = false;
	@Input() public aiStyle: boolean = false;
	@Input() public withName: boolean = false;
	@Input() public verticalLayout = false;

	private readonly BASE_MARKER_WIDTH = 43;
	private readonly CHAR_WIDTH = 6;
	private readonly MAX_LINES = 3;
	private readonly OVERFLOW_INDICATOR_WIDTH = 46;

	public readonly visibleMarkers$ = new BehaviorSubject<MarkerDisplayItem[]>([]);
	public readonly hiddenCount$ = new BehaviorSubject<number>(0);
	public readonly showOverflow$ = new BehaviorSubject<boolean>(false);

	constructor (
		private elementRef: ElementRef,
		private featureFlagService: NgxFeatureFlagService
	) {}

	public ngAfterViewInit (): void {
		this.calculateVisibleMarkers();
	}

	public ngOnChanges (changes: SimpleChanges): void {
		if (changes.markers) {
			this.calculateVisibleMarkers();
		}
	}

	private calculateMarkerWidth (marker: MarkerDisplayItem): number {
		const displayText = marker.tag_name;
		const textWidth = displayText.length * this.CHAR_WIDTH;

		return this.BASE_MARKER_WIDTH + textWidth;
	}

	private calculateVisibleMarkers (): void {
		if (!this.markers || !this.markers.length || !this.aiStyle || this.verticalLayout || !this.featureFlagService.isAvailable('AI_CONFIG')) {
			this.visibleMarkers$.next(this.markers);
			this.hiddenCount$.next(0);
			this.showOverflow$.next(false);
			return;
		}

		const sortedMarkers = [...this.markers].sort((a, b) => {
			return AI_MARKER_CATEGORY_ORDER.indexOf(a.category?.short_name) -
				AI_MARKER_CATEGORY_ORDER.indexOf(b.category?.short_name);
		});

		const container = this.elementRef.nativeElement;
		let containerWidth = container.offsetWidth;
		if (containerWidth === 0) {
			// if accordion is collapsed, use the accordion width minus padding
			const accordion = container.closest('.accordion');
			if (accordion) {
				containerWidth = accordion.offsetWidth - 20;
			}
		}

		let visibleMarkers = this.calculateMarkersForWidth(sortedMarkers, containerWidth);
		const needsOverflow = visibleMarkers.length < sortedMarkers.length;

		if (needsOverflow) {
			const availableWidth = containerWidth - this.OVERFLOW_INDICATOR_WIDTH;
			visibleMarkers = this.calculateMarkersForWidth(sortedMarkers, availableWidth);
		}

		const hiddenCount = Math.max(0, this.markers.length - visibleMarkers.length);
		const showOverflow = hiddenCount > 0;

		this.visibleMarkers$.next(visibleMarkers);
		this.hiddenCount$.next(hiddenCount);
		this.showOverflow$.next(showOverflow);
	}

	private calculateMarkersForWidth (markers: MarkerDisplayItem[], availableWidth: number): MarkerDisplayItem[] {
		let currentLineWidth = 0;
		let currentLine = 0;
		const visibleMarkers: MarkerDisplayItem[] = [];

		for (const marker of markers) {
			const markerWidth = this.calculateMarkerWidth(marker);

			if (currentLineWidth + markerWidth <= availableWidth) {
				currentLineWidth += markerWidth;
			} else {
				currentLine++;
				currentLineWidth = markerWidth;
			}

			if (currentLine > this.MAX_LINES) {
				break;
			}

			visibleMarkers.push(marker);
		}

		return visibleMarkers;
	}
}
